// Purchasing contracts (docs/use-cases/purchasing.md, UC-PURCHASING-001..008; modules-and-api.md §5.15; working decisions W6,
// W9, W14 and W15 of docs/sprints/sprint-04.md).
//
// Sprint 4 foundation: the vocabularies the supplier master, the lab lines and the receipt without a purchase order share —
// in their tables' CHECK constraints and on their routes — spelled as the use cases spell them, and their limits. The goods
// receipt's status machine is in status-machines.ts; quantities, unit costs, discounts and the supplier's-serial format in
// primitives.ts; the arithmetic in `@visionx/money` (`timesQuantity`, `netOfDiscount`, `weightedAverageCost`).
//
// The supplier master's request and response schemas (UC-PURCHASING-001..004, -006, -007), added with its routes: the fields at
// their boundaries (TC-PURCHASING-002), the rules that read several fields together — shared by the add, the merged result of
// an edit and the PWA's form — the views, the list and lab-line queries, and the kinds of record that keep a supplier from being
// deleted. The receipt's schemas (UC-PURCHASING-005, -008), added with its routes (Sprint 4 stage 5), close the file.
//
// The catalogue's and pricing's vocabularies are mirrored here by value and checked against their types (`import type` only), so
// no Sprint 4 contracts file imports another at run time: two of them importing each other would be a module cycle.
import { z } from "zod";
import type { LensDesignType } from "./catalogue";
import { UserRefSchema } from "./governance";
import type { SerialSource } from "./inventory";
import { normaliseMobile } from "./patients";
import type { SupplierPriceListStatus } from "./pricing";
import { EmailSchema, InstantSchema, LINE_QUANTITY_MAX, LocalDateSchema, SupplierSerialSchema, UNIT_COST_MAX_CENTS, UlidSchema } from "./primitives";
import { GOODS_RECEIPT_STATUSES } from "./status-machines";

/**
 * BR-PURCHASING-003 (LAB-03): a supplier carries one or more types, each at most once. Sprint 4 labels and filters with them;
 * Sprint 7 reads them for lab orders and purchase orders.
 */
export const SUPPLIER_TYPES = ["LensLab", "FittingLab", "Frame", "ContactLens", "Accessory", "Other"] as const;
export type SupplierType = (typeof SUPPLIER_TYPES)[number];
export const SUPPLIER_TYPE_LABELS: Readonly<Record<SupplierType, string>> = {
  LensLab: "Lens lab", FittingLab: "Fitting/edging lab", Frame: "Frame supplier", ContactLens: "Contact-lens supplier",
  Accessory: "Accessory supplier", Other: "Other",
};

/** BR-PURCHASING-009: a supplier is Active when added and may be deactivated and reactivated. */
export const SUPPLIER_STATUSES = ["Active", "Inactive"] as const;
export type SupplierStatus = (typeof SUPPLIER_STATUSES)[number];
/** BR-PURCHASING-021: the suppliers list's `?status` — `Active` by default, `Inactive`, or `All` (every supplier not deleted). */
export const SUPPLIER_STATUS_FILTERS = ["Active", "Inactive", "All"] as const;
export type SupplierStatusFilter = (typeof SUPPLIER_STATUS_FILTERS)[number];

/**
 * BR-PURCHASING-005: how Sprint 7's purchase orders reach the supplier — `EmailPdf` (the default) or `Print`. `Hoyalog` is a
 * roadmap value (LAB-05, LAB-71, A17): stored vocabulary, refused on every write (`orderChannel`, `not_available`).
 */
export const ORDER_CHANNELS = ["EmailPdf", "Print", "Hoyalog"] as const;
export type OrderChannel = (typeof ORDER_CHANNELS)[number];
export const AVAILABLE_ORDER_CHANNELS = ["EmailPdf", "Print"] as const satisfies readonly OrderChannel[];
export const ORDER_CHANNEL_LABELS: Readonly<Record<OrderChannel, string>> = { EmailPdf: "Email PDF", Print: "Print", Hoyalog: "Hoyalog" };

/** BR-PURCHASING-008 (LAB-05): the Hoyalog data hooks — a manufacturer code, `none` by default; enabled only with `hoya` or `seiko`. */
export const HOYALOG_MANUFACTURER_CODES = ["hoya", "seiko", "none"] as const;
export type HoyalogManufacturerCode = (typeof HOYALOG_MANUFACTURER_CODES)[number];

/**
 * BR-PURCHASING-020: the kind of a supplier's lab line — in Sprint 4 its lenses, the only lines Sprint 4's import layouts
 * carry; coatings, colours and PPF where a later layout carries them (CAT-47). A fitting lab's fee list waits with the
 * fitting-fee rules.
 */
export const LAB_LINE_KINDS = ["Lens", "Coating", "Colour", "PPF"] as const;
export type LabLineKind = (typeof LAB_LINE_KINDS)[number];
/** BR-CATALOGUE-008's five lens designs, as a lab line names them (the catalogue's `LENS_DESIGN_TYPES`, the schema's `LAB_LINE_DESIGN_TYPES`). */
export const LAB_LINE_DESIGN_TYPES = ["SingleVision", "Bifocal", "Trifocal", "Progressive", "Occupational"] as const satisfies readonly LensDesignType[];
/** BR-PRICING-019: a price list's status, as a lab line's list shows it (pricing's `SUPPLIER_PRICE_LIST_STATUSES`). */
const PRICE_LIST_STATUSES = ["Draft", "Current", "Superseded"] as const satisfies readonly SupplierPriceListStatus[];

/** The limits the purchasing use cases state (BR-PURCHASING-002..008, -010, -014, -015, -016). */
export const PURCHASING_LIMITS = {
  /** A supplier: name 1–120 (unique ignoring case), contact 120, email 254, notes 2 000, up to four copied order addresses. */
  supplierNameMax: 120, contactNameMax: 120, emailMax: 254, supplierNotesMax: 2_000, orderEmailCcMax: 4,
  /** Lead time 0–60 working days and payment terms 0–365 days, both optional; a VAT number at most 20 characters. */
  leadTimeDaysMax: 60, paymentTermsDaysMax: 365, vatNumberMax: 20,
  /** A lens catalogue code: lower-case letters and digits, at most 32 (D30). */
  lensCatalogueCodeMax: 32,
  /** A deletion's reason, 1–500 characters (W15). */
  reasonMax: 500,
  /** A receipt: 1–50 lines, at most 100 serialised units, a reason 1–500, a delivery note 64, notes 2 000. */
  receiptLinesMin: 1, receiptLinesMax: 50, receiptSerialisedUnitsMax: 100, deliveryNoteMax: 64, receiptNotesMax: 2_000,
} as const;

// --- The supplier's fields (BR-PURCHASING-002..008; TC-PURCHASING-002) ---------------------------------------------------------

const text = (max: number) => z.string().trim().max(max);
const optionalText = (max: number) => text(max).nullable().optional();
const Limit = z.coerce.number().int().min(1).max(200).default(50);
const Flag = z.enum(["true", "false"]);
/** A country as a two-letter ISO code in capitals (`ZA`): `za` and `ZAF` are refused, never corrected. */
const CountryCodeSchema = z.string().trim().regex(/^[A-Z]{2}$/, "a two-letter country code in capitals, such as ZA");

/** BR-PURCHASING-002: 1–120 characters, trimmed; unique in the organisation ignoring case, which the server checks (`not_unique`). */
export const SupplierNameSchema = z.string().trim().min(1, "a name is required").max(PURCHASING_LIMITS.supplierNameMax);
/**
 * BR-PURCHASING-004: a South African number in its 0, +27 or 0027 form, or a foreign number with its + country code — the rule
 * the patient's mobile follows (`normaliseMobile`, BR-16). Kept as typed; an empty value stores none.
 */
export const SupplierPhoneSchema = z.string().trim().max(40)
  .refine((v) => v === "" || normaliseMobile(v) !== null, { message: "not a phone number", params: { code: "invalid" } });
/** BR-PURCHASING-004: a structured address; the country a two-letter ISO code, `ZA` by default. */
export const SupplierAddressInputSchema = z.strictObject({
  line1: optionalText(200), line2: optionalText(200), city: optionalText(120), province: optionalText(120), postalCode: optionalText(20),
  country: CountryCodeSchema.optional(),
});
export type SupplierAddressInput = z.infer<typeof SupplierAddressInputSchema>;
/** BR-PURCHASING-008: data hooks only (A17) — enabled only with `hoya` or `seiko`; the country code two upper-case letters, `SA` by default. */
export const SupplierHoyalogInputSchema = z.strictObject({
  enabled: z.boolean().optional(),
  manufacturerCode: z.enum(HOYALOG_MANUFACTURER_CODES).optional(),
  countryCode: CountryCodeSchema.optional(),
});
/** BR-PURCHASING-008 (D30): the platform lens catalogue's manufacturer — lower-case letters and digits, at most 32 — checked against nothing. */
export const LensCatalogueCodeSchema = z.string().trim().min(1).max(PURCHASING_LIMITS.lensCatalogueCodeMax)
  .regex(/^[a-z0-9]+$/, "lower-case letters and digits only");

/** The fields every supplier rule reads together: a whole supplier as an add sends it, or as an edit leaves it. */
export interface SupplierRuleFields {
  types: readonly string[];
  orderChannel: OrderChannel;
  orderEmail: string | null | undefined;
  orderEmailCc: readonly string[];
  isVatRegistered: boolean;
  vatNumber: string | null | undefined;
  hoyalog: { enabled: boolean; manufacturerCode: HoyalogManufacturerCode };
}
export interface SupplierRuleIssue { path: (string | number)[]; code: string; message: string }

/**
 * The rules that read several fields of a supplier at once (BR-PURCHASING-003, -005, -006, -008), checked on an add and on the
 * record an edit would leave (UC-PURCHASING-002 E8): each type at most once (`types`, `duplicate`); `Hoyalog` is a roadmap
 * channel (`orderChannel`, `not_available`); Email PDF needs its order email (`orderEmail`, `required`); a copied address once,
 * and never the order email itself (`orderEmailCc.N`, `duplicate`); a VAT-registered supplier its VAT number (`vatNumber`,
 * `required`); Hoyalog enabled only with a manufacturer (`hoyalog.manufacturerCode`, `required`).
 */
export function supplierRuleIssues(r: SupplierRuleFields): SupplierRuleIssue[] {
  const out: SupplierRuleIssue[] = [];
  if (new Set(r.types).size !== r.types.length) out.push({ path: ["types"], code: "duplicate", message: "a type appears more than once" });
  if (r.orderChannel === "Hoyalog") out.push({ path: ["orderChannel"], code: "not_available", message: "Hoyalog ordering is on the roadmap and cannot be chosen yet (LAB-71)" });
  if (r.orderChannel === "EmailPdf" && !r.orderEmail) out.push({ path: ["orderEmail"], code: "required", message: "an order email is required to order by Email PDF" });
  const seen = new Set<string>(r.orderEmail ? [r.orderEmail.toLowerCase()] : []);
  r.orderEmailCc.forEach((cc, i) => {
    const key = cc.toLowerCase();
    if (seen.has(key)) out.push({ path: ["orderEmailCc", i], code: "duplicate", message: "this address is already on the order" });
    seen.add(key);
  });
  if (r.isVatRegistered && !r.vatNumber) out.push({ path: ["vatNumber"], code: "required", message: "a VAT-registered supplier needs its VAT number" });
  if (r.hoyalog.enabled && r.hoyalog.manufacturerCode === "none") out.push({ path: ["hoyalog", "manufacturerCode"], code: "required", message: "Hoyalog can be enabled only for Hoya or Seiko" });
  return out;
}

/** The field schemas an add and an edit share; the add gives them their defaults. */
const supplierFields = {
  name: SupplierNameSchema,
  types: z.array(z.enum(SUPPLIER_TYPES)).min(1, "at least one type").max(SUPPLIER_TYPES.length),
  contactName: optionalText(PURCHASING_LIMITS.contactNameMax),
  phone: SupplierPhoneSchema.nullable().optional(),
  email: EmailSchema.nullable().optional(),
  address: SupplierAddressInputSchema.optional(),
  orderChannel: z.enum(ORDER_CHANNELS),
  orderEmail: EmailSchema.nullable().optional(),
  orderEmailCc: z.array(EmailSchema).max(PURCHASING_LIMITS.orderEmailCcMax),
  leadTimeDays: z.number().int().min(0).max(PURCHASING_LIMITS.leadTimeDaysMax).nullable().optional(),
  paymentTermsDays: z.number().int().min(0).max(PURCHASING_LIMITS.paymentTermsDaysMax).nullable().optional(),
  isVatRegistered: z.boolean(),
  vatNumber: z.string().trim().min(1).max(PURCHASING_LIMITS.vatNumberMax).nullable().optional(),
  /** BR-PURCHASING-007: whole basis points 0–10 000 (0–100 %). */
  defaultDiscountBp: z.number().int().min(0).max(10_000),
  hoyalog: SupplierHoyalogInputSchema.optional(),
  lensCatalogueCode: LensCatalogueCodeSchema.nullable().optional(),
  notes: optionalText(PURCHASING_LIMITS.supplierNotesMax),
};

/**
 * `POST /suppliers` (UC-PURCHASING-001): Email PDF, VAT-registered, a 0 % discount, no copied addresses and Hoyalog off with
 * `none` and `SA` unless the body says otherwise.
 */
export const SupplierInputSchema = z.strictObject({
  ...supplierFields,
  orderChannel: supplierFields.orderChannel.default("EmailPdf"),
  orderEmailCc: supplierFields.orderEmailCc.default([]),
  isVatRegistered: supplierFields.isVatRegistered.default(true),
  defaultDiscountBp: supplierFields.defaultDiscountBp.default(0),
}).superRefine((v, ctx) => {
  const issues = supplierRuleIssues({
    types: v.types, orderChannel: v.orderChannel, orderEmail: v.orderEmail, orderEmailCc: v.orderEmailCc, isVatRegistered: v.isVatRegistered, vatNumber: v.vatNumber,
    hoyalog: { enabled: v.hoyalog?.enabled ?? false, manufacturerCode: v.hoyalog?.manufacturerCode ?? "none" },
  });
  for (const i of issues) ctx.addIssue({ code: "custom", path: i.path, message: i.message, params: { code: i.code } });
});
export type SupplierInput = z.infer<typeof SupplierInputSchema>;

/**
 * `PATCH /suppliers/{id}` (UC-PURCHASING-002): any of the add's fields, each optional; the address and the Hoyalog data change
 * key by key. The rules that read several fields are checked by the server on the record the edit would leave
 * (`supplierRuleIssues`); the status, the id and the stamps are refused as `read_only` before the body is read.
 */
export const SupplierPatchSchema = z.strictObject({
  name: supplierFields.name.optional(),
  types: supplierFields.types.optional(),
  contactName: supplierFields.contactName,
  phone: supplierFields.phone,
  email: supplierFields.email,
  address: supplierFields.address,
  orderChannel: supplierFields.orderChannel.optional(),
  orderEmail: supplierFields.orderEmail,
  orderEmailCc: supplierFields.orderEmailCc.optional(),
  leadTimeDays: supplierFields.leadTimeDays,
  paymentTermsDays: supplierFields.paymentTermsDays,
  isVatRegistered: supplierFields.isVatRegistered.optional(),
  vatNumber: supplierFields.vatNumber,
  defaultDiscountBp: supplierFields.defaultDiscountBp.optional(),
  hoyalog: supplierFields.hoyalog,
  lensCatalogueCode: supplierFields.lensCatalogueCode,
  notes: supplierFields.notes,
});
export type SupplierPatch = z.infer<typeof SupplierPatchSchema>;
/** UC-PURCHASING-002 E5: fields no edit may carry — the status changes only by UC-PURCHASING-004; the id and the stamps never. */
export const SUPPLIER_READ_ONLY_FIELDS = [
  "id", "status", "version", "createdAt", "createdBy", "updatedAt", "deactivatedAt", "deactivatedBy", "deactivationReason",
  "voidedAt", "voidedBy", "voidReason", "branchAccounts",
] as const;

/** `POST /suppliers/{id}/deactivate` and `/activate` (UC-PURCHASING-004): an optional reason of at most 500 characters. */
export const SupplierStatusChangeInputSchema = z.strictObject({ reason: z.string().trim().max(PURCHASING_LIMITS.reasonMax).nullable().optional() });
export type SupplierStatusChangeInput = z.infer<typeof SupplierStatusChangeInputSchema>;
/** `POST /suppliers/{id}/void` (UC-PURCHASING-007): the reason, 1–500 characters. */
export const SupplierVoidInputSchema = z.strictObject({ reason: z.string().trim().min(1).max(PURCHASING_LIMITS.reasonMax) });

// --- The supplier as read (UC-PURCHASING-003) ---------------------------------------------------------------------------------

export const SupplierAddressSchema = z.object({
  line1: z.string().nullable(), line2: z.string().nullable(), city: z.string().nullable(), province: z.string().nullable(), postalCode: z.string().nullable(),
  country: z.string(),
});
export const SupplierHoyalogSchema = z.object({ enabled: z.boolean(), manufacturerCode: z.enum(HOYALOG_MANUFACTURER_CODES), countryCode: z.string() });
/** A branch's account with the supplier (UC-TENANCY-010), of a branch the reader may read. */
export const SupplierBranchAccountSchema = z.object({
  id: UlidSchema,
  branch: z.object({ id: UlidSchema, code: z.string(), name: z.string() }),
  accountNumber: z.string(),
  isDefaultForLenses: z.boolean(),
  isDefaultForFitting: z.boolean(),
});
export const SupplierViewSchema = z.object({
  id: UlidSchema,
  name: z.string(),
  types: z.array(z.enum(SUPPLIER_TYPES)),
  contactName: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  address: SupplierAddressSchema,
  orderChannel: z.enum(ORDER_CHANNELS),
  orderEmail: z.string().nullable(),
  orderEmailCc: z.array(z.string()),
  leadTimeDays: z.number().int().nullable(),
  paymentTermsDays: z.number().int().nullable(),
  isVatRegistered: z.boolean(),
  vatNumber: z.string().nullable(),
  defaultDiscountBp: z.number().int(),
  hoyalog: SupplierHoyalogSchema,
  lensCatalogueCode: z.string().nullable(),
  notes: z.string().nullable(),
  status: z.enum(SUPPLIER_STATUSES),
  deactivatedAt: InstantSchema.nullable(),
  deactivatedBy: UserRefSchema.nullable(),
  deactivationReason: z.string().nullable(),
  /** The accounts of the branches the reader may read, by branch name; the list says the acting branch's alone. */
  branchAccounts: z.array(SupplierBranchAccountSchema),
  version: z.number().int(),
  createdAt: InstantSchema,
  createdBy: UserRefSchema.nullable(),
  updatedAt: InstantSchema,
});
export type SupplierView = z.infer<typeof SupplierViewSchema>;
export const SupplierStatusChangeResultSchema = SupplierViewSchema.extend({ changed: z.boolean() });
export type SupplierStatusChangeResult = z.infer<typeof SupplierStatusChangeResultSchema>;
/** The answer to a deletion (UC-PURCHASING-007 step 5): who voided it, when, why, and the Control Log row when a PIN was asked. */
export const SupplierVoidResultSchema = z.object({
  id: UlidSchema, name: z.string(), voidedAt: InstantSchema, voidedBy: UserRefSchema.nullable(), reason: z.string(), controlLogId: UlidSchema.nullable(),
});
export type SupplierVoidResult = z.infer<typeof SupplierVoidResultSchema>;

/** A row of Purchasing › Suppliers and of every supplier picker (UC-PURCHASING-003 step 2). */
export const SupplierListItemSchema = z.object({
  id: UlidSchema,
  name: z.string(),
  types: z.array(z.enum(SUPPLIER_TYPES)),
  status: z.enum(SUPPLIER_STATUSES),
  contactName: z.string().nullable(),
  phone: z.string().nullable(),
  orderChannel: z.enum(ORDER_CHANNELS),
  leadTimeDays: z.number().int().nullable(),
  /** The acting branch's account number with the supplier; null without one, or with no acting branch. */
  accountNumber: z.string().nullable(),
});
export type SupplierListItem = z.infer<typeof SupplierListItemSchema>;
/**
 * `GET /suppliers` (BR-PURCHASING-021): `q` matches the start of any word of the name ignoring case, from one character, never a
 * substring; `type` one of the six; `status` Active by default, Inactive or All; by name, then id; keyset cursor.
 */
export const SupplierListQuerySchema = z.object({
  q: z.string().trim().max(PURCHASING_LIMITS.supplierNameMax).optional(),
  type: z.enum(SUPPLIER_TYPES).optional(),
  status: z.enum(SUPPLIER_STATUS_FILTERS).default("Active"),
  limit: Limit,
  cursor: z.string().max(512).optional(),
  count: Flag.optional(),
});
export type SupplierListQuery = z.infer<typeof SupplierListQuerySchema>;

// --- What keeps a supplier from being deleted (BR-PURCHASING-010) -----------------------------------------------------------------

/**
 * The kinds of record that name a supplier and so keep it from being deleted as recorded in error, in the order
 * `meta.blockingRecordTypes` lists them. Sprint 7's purchase orders, bills and returns join when they are built.
 */
export const SUPPLIER_REFERENCE_KINDS = [
  "product", "supplier_price_list", "supplier_lens_code", "goods_receipt", "serial", "lens_component_option", "data_import_job", "frame_markup_band",
  "branch_supplier_account",
] as const;
export type SupplierReferenceKind = (typeof SUPPLIER_REFERENCE_KINDS)[number];
/** What the deletion dialog says for each kind (UC-PURCHASING-007 E1: "names the kinds in words"). */
export const SUPPLIER_REFERENCE_KIND_LABELS: Readonly<Record<SupplierReferenceKind, string>> = {
  product: "products", supplier_price_list: "price lists", supplier_lens_code: "lab lines", goods_receipt: "goods receipts", serial: "serials",
  lens_component_option: "lens component options", data_import_job: "price-list imports", frame_markup_band: "markup bands",
  branch_supplier_account: "branch supplier accounts",
};

// --- A supplier's lab lines (UC-PURCHASING-006; BR-PURCHASING-020) ---------------------------------------------------------------

/** A line is in its list in force on the day, or — once a newer list without its code is in force — flagged (CAT-46, R-10). */
export const LAB_LINE_STATUSES = ["Current", "NotInLatestPriceList"] as const;
export type LabLineStatus = (typeof LAB_LINE_STATUSES)[number];
export const LAB_LINE_STATUS_LABELS: Readonly<Record<LabLineStatus, string>> = { Current: "Current", NotInLatestPriceList: "Not in latest price list" };

/** A calendar day `YYYY-MM-DD` that exists (`2026-02-30` does not). */
const CalendarDaySchema = LocalDateSchema.refine((d) => {
  const t = Date.parse(`${d}T00:00:00Z`);
  return Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === d;
}, { message: "not a calendar day", params: { code: "invalid" } });

/**
 * `GET /suppliers/{id}/lab-lines`: the lines in force on `asAt` (today by default) at the acting branch; `q` a code's start or a
 * word of the description; `kind`; `design`; `index` the refraction index as written on the lens (`1.67`).
 */
export const LabLinesQuerySchema = z.object({
  asAt: CalendarDaySchema.optional(),
  q: z.string().trim().max(60).optional(),
  kind: z.enum(LAB_LINE_KINDS).optional(),
  design: z.enum(LAB_LINE_DESIGN_TYPES).optional(),
  index: z.string().trim().regex(/^[12]\.\d{1,3}$/, "a refraction index such as 1.67").optional(),
  limit: Limit,
  cursor: z.string().max(512).optional(),
  count: Flag.optional(),
});
export type LabLinesQuery = z.infer<typeof LabLinesQuerySchema>;

export const LabLineViewSchema = z.object({
  id: UlidSchema,
  code: z.string(),
  kind: z.enum(LAB_LINE_KINDS),
  description: z.string().nullable(),
  rangeName: z.string().nullable(),
  designType: z.enum(LAB_LINE_DESIGN_TYPES).nullable(),
  lensType: z.string().nullable(),
  /** The index in thousandths, as the catalogue keeps it (1.74 → 1 740). */
  refractionIndexX1000: z.number().int().nullable(),
  /** List price, the discount it was recorded with and the net cost, ex VAT (W1, R-08). */
  listCostCents: z.number().int().nullable(),
  discountBp: z.number().int().nullable(),
  netCostCents: z.number().int().nullable(),
  recommendedRetailCents: z.number().int().nullable(),
  /** The list's date: the line's cost is in force from it. */
  effectiveFrom: z.string(),
  priceList: z.object({
    id: UlidSchema, name: z.string(), effectiveFrom: z.string(), status: z.enum(PRICE_LIST_STATUSES),
    /** Null: the organisation's list; else one branch's own (LAB-84). */
    branch: z.object({ id: UlidSchema, code: z.string(), name: z.string() }).nullable(),
  }),
  status: z.enum(LAB_LINE_STATUSES),
  /** Whether this line is the cost at the acting branch: its own list's line in force, else the organisation's (BR-PRICING-020). */
  appliesHere: z.boolean(),
  product: z.object({ id: UlidSchema, sku: z.string(), name: z.string() }).nullable(),
  /** The import and the row of its file the line came from (BR-IMPORTS-017); null for a line no import wrote. */
  source: z.object({ importJobId: UlidSchema, fileName: z.string().nullable(), rowNo: z.number().int().nullable() }).nullable(),
});
export type LabLineView = z.infer<typeof LabLineViewSchema>;

// --- The goods receipt without a purchase order (UC-PURCHASING-005, -008; BR-PURCHASING-014..019, -021) -------------------------
// Money is whole cents: a unit cost is ex VAT and net (W1, R-08), a kept price VAT-inclusive. A field refused by the server is
// named by its API path — `lines.0.priceReason`, `lines.0.units.2.supplierSerial` — as the screens show it under the field.

/** BR-PURCHASING-017: what proposed a line's price — a frame's markup band, a product markup, or nothing (`no_markup`). */
export const RECEIPT_PROPOSAL_SOURCES = ["Band", "ProductMarkup", "None"] as const;
export type ReceiptProposalSource = (typeof RECEIPT_PROPOSAL_SOURCES)[number];
/** BR-PURCHASING-016: a unit's serial is the tenant sequencer's (`2026-09-07-001`) or the supplier's own (inventory's `SERIAL_SOURCES`). */
export const RECEIPT_SERIAL_SOURCES = ["Generated", "SupplierSerial"] as const satisfies readonly SerialSource[];
/** UC-PURCHASING-005 A9, A14: what a posted receipt warns of — a line no band or markup proposes, a delivery note already received. */
export const RECEIPT_WARNING_CODES = ["no_markup", "duplicate_delivery_note"] as const;
export type ReceiptWarningCode = (typeof RECEIPT_WARNING_CODES)[number];

/** A kept selling price, VAT-inclusive whole cents (W1), R 0.00–R 999 999.99. */
const KeptPriceSchema = z.number().int().min(0).max(UNIT_COST_MAX_CENTS);
/** A kept price's reason, at most 500 characters; blank is none (BR-PURCHASING-017). */
const PriceReasonSchema = z.string().trim().max(PURCHASING_LIMITS.reasonMax);
/** An id a body names: any text of 1–64 characters, so an id that names nothing is `404 NOT_FOUND` as a foreign one is (BR-PURCHASING-001). */
const BodyIdSchema = z.string().trim().min(1).max(64);

/**
 * One unit of a serialised line (BR-PURCHASING-016, -017): the supplier's own serial as scanned or typed — 1–64 characters, never
 * of the generated shape (`reserved_format`, checked before anything else) — and the unit's kept price with its reason, which
 * take the place of the line's.
 */
export const GoodsReceiptUnitInputSchema = z.strictObject({
  supplierSerial: SupplierSerialSchema.nullable().optional(),
  keptPriceCents: KeptPriceSchema.nullable().optional(),
  priceReason: PriceReasonSchema.nullable().optional(),
});
export type GoodsReceiptUnitInput = z.infer<typeof GoodsReceiptUnitInputSchema>;

/**
 * A receipt line (BR-PURCHASING-015): a stocked product, a whole quantity 1–9 999 (W4), the unit cost ex VAT and net, R 0.00–
 * R 999 999.99; the kept price — the proposal's unless typed — and its reason; a serialised line's unit entries, one per unit
 * when given, each with its own serial and its own price when it differs from the line's.
 */
export const GoodsReceiptLineInputSchema = z.strictObject({
  productId: BodyIdSchema,
  quantity: z.number().int().min(1).max(LINE_QUANTITY_MAX),
  unitCostCents: z.number().int().min(0).max(UNIT_COST_MAX_CENTS),
  keptPriceCents: KeptPriceSchema.nullable().optional(),
  priceReason: PriceReasonSchema.nullable().optional(),
  units: z.array(GoodsReceiptUnitInputSchema).max(LINE_QUANTITY_MAX).optional(),
});
export type GoodsReceiptLineInput = z.infer<typeof GoodsReceiptLineInputSchema>;

/**
 * `POST /goods-receipts` (UC-PURCHASING-005; BR-PURCHASING-014, -015): at the acting branch, from an Active supplier, with no
 * purchase order and so a reason (1–500), the supplier's delivery note (≤ 64), notes (≤ 2 000), the received date — today in the
 * branch's timezone by default, never later — and 1–50 lines.
 */
export const GoodsReceiptInputSchema = z.strictObject({
  supplierId: BodyIdSchema,
  receivedDate: CalendarDaySchema.optional(),
  deliveryNoteNumber: z.string().trim().max(PURCHASING_LIMITS.deliveryNoteMax).nullable().optional(),
  reason: z.string().trim().min(1, "a reason is required: the receipt has no purchase order").max(PURCHASING_LIMITS.reasonMax),
  notes: z.string().trim().max(PURCHASING_LIMITS.receiptNotesMax).nullable().optional(),
  lines: z.array(GoodsReceiptLineInputSchema).min(PURCHASING_LIMITS.receiptLinesMin).max(PURCHASING_LIMITS.receiptLinesMax),
});
export type GoodsReceiptInput = z.infer<typeof GoodsReceiptInputSchema>;

/** The proposal a line froze at posting (BR-PURCHASING-017): its band or product markup, percentage and value, the proposal and the price in force. */
export const ReceiptProposalSchema = z.object({
  source: z.enum(RECEIPT_PROPOSAL_SOURCES),
  bandId: z.string().nullable(),
  productMarkupId: z.string().nullable(),
  marginPercentBp: z.number().int().nullable(),
  marginValueCents: z.number().int().nullable(),
  /** The VAT-inclusive proposal; null with no band or markup (`no_markup`). */
  proposedCents: z.number().int().nullable(),
  /** The price the branch charged for the product that day, without a serial (UC-PRICING-008); null for none. */
  currentCents: z.number().int().nullable(),
});
export type ReceiptProposal = z.infer<typeof ReceiptProposalSchema>;

/** A received unit as posted: its serial, its receipt cost (reference only, W5), its kept price, reason and own price — and whether its serial was deleted since (A4). */
export const GoodsReceiptUnitViewSchema = z.object({
  id: UlidSchema,
  serialId: UlidSchema,
  serialNumber: z.string(),
  source: z.enum(RECEIPT_SERIAL_SOURCES),
  costCents: z.number().int(),
  keptPriceCents: z.number().int().nullable(),
  priceReason: z.string().nullable(),
  keptBy: UserRefSchema.nullable(),
  /** The selling price posting gave the serial itself (BR-PRICING-016's third case); null when it sells at its branch's price. */
  ownPriceCents: z.number().int().nullable(),
  controlLogId: UlidSchema.nullable(),
  /** A serial deleted as recorded in error after posting (UC-INVENTORY-005): the receipt still lists it, marked (UC-PURCHASING-008 A4). */
  serialVoided: z.boolean(),
  serialVoidedAt: InstantSchema.nullable(),
  serialVoidedBy: UserRefSchema.nullable(),
  serialVoidReason: z.string().nullable(),
});
export type GoodsReceiptUnitView = z.infer<typeof GoodsReceiptUnitViewSchema>;

export const GoodsReceiptLineViewSchema = z.object({
  id: UlidSchema,
  lineNo: z.number().int(),
  productId: UlidSchema,
  /** Frozen at posting (R-23). */
  sku: z.string(),
  description: z.string(),
  quantity: z.number().int(),
  unitCostCents: z.number().int(),
  lineTotalExVatCents: z.number().int(),
  /** Null when the branch did not propose prices at receipt (BR-PRICING-018): nothing was offered. */
  proposal: ReceiptProposalSchema.nullable(),
  /** A line without serials: its kept price, reason and who kept it; a serialised line's are its units'. */
  keptPriceCents: z.number().int().nullable(),
  priceReason: z.string().nullable(),
  keptBy: UserRefSchema.nullable(),
  /** What the line's prices recorded besides its units' own: the product's first tenant price, a branch price from the day of posting. */
  tenantPriceCents: z.number().int().nullable(),
  branchPriceId: UlidSchema.nullable(),
  controlLogId: UlidSchema.nullable(),
  units: z.array(GoodsReceiptUnitViewSchema),
});
export type GoodsReceiptLineView = z.infer<typeof GoodsReceiptLineViewSchema>;

const ReceiptBranchSchema = z.object({ id: UlidSchema, code: z.string(), name: z.string() });

/** One receipt as posted (UC-PURCHASING-008 step 4); never changed (BR-PURCHASING-014). */
export const GoodsReceiptViewSchema = z.object({
  id: UlidSchema,
  number: z.string(),
  status: z.enum(GOODS_RECEIPT_STATUSES),
  branch: ReceiptBranchSchema,
  /** The supplier's current name (an internal document, UC-PURCHASING-008 open questions) and the receipt's branch's account with it. */
  supplier: z.object({ id: UlidSchema, name: z.string(), accountNumber: z.string().nullable() }),
  /** Always null in Sprint 4 (W9): a receipt against a purchase order is Sprint 7's. */
  purchaseOrderId: z.string().nullable(),
  reason: z.string().nullable(),
  deliveryNoteNumber: z.string().nullable(),
  notes: z.string().nullable(),
  receivedDate: z.string(),
  receivedBy: UserRefSchema.nullable(),
  postedAt: InstantSchema,
  postedBy: UserRefSchema.nullable(),
  /** The sum of the lines, ex VAT: a receipt calculates no VAT (BR-PURCHASING-015). */
  totalExVatCents: z.number().int(),
  lineCount: z.number().int(),
  unitCount: z.number().int(),
  lines: z.array(GoodsReceiptLineViewSchema),
});
export type GoodsReceiptView = z.infer<typeof GoodsReceiptViewSchema>;

/** A posted receipt's warnings (A9, A14): a line with no band or markup; a delivery note already on another receipt from the supplier. */
export const GoodsReceiptWarningSchema = z.object({
  path: z.string(),
  code: z.enum(RECEIPT_WARNING_CODES),
  message: z.string(),
  receiptId: UlidSchema.optional(),
  receiptNumber: z.string().optional(),
  receivedDate: z.string().optional(),
});
export type GoodsReceiptWarning = z.infer<typeof GoodsReceiptWarningSchema>;
/** `POST /goods-receipts`' answer: the receipt as posted and what it warns of. */
export const GoodsReceiptWriteResultSchema = GoodsReceiptViewSchema.extend({ warnings: z.array(GoodsReceiptWarningSchema) });
export type GoodsReceiptWriteResult = z.infer<typeof GoodsReceiptWriteResultSchema>;

/** A row of Purchasing › Goods receipts (UC-PURCHASING-008 step 2). */
export const GoodsReceiptListItemSchema = z.object({
  id: UlidSchema,
  number: z.string(),
  status: z.enum(GOODS_RECEIPT_STATUSES),
  branch: ReceiptBranchSchema,
  supplier: z.object({ id: UlidSchema, name: z.string() }),
  purchaseOrderId: z.string().nullable(),
  reason: z.string().nullable(),
  deliveryNoteNumber: z.string().nullable(),
  receivedDate: z.string(),
  receivedBy: UserRefSchema.nullable(),
  lineCount: z.number().int(),
  unitCount: z.number().int(),
  totalExVatCents: z.number().int(),
  postedAt: InstantSchema,
});
export type GoodsReceiptListItem = z.infer<typeof GoodsReceiptListItemSchema>;

/**
 * `GET /goods-receipts` (BR-PURCHASING-021): newest first — received date, then number — at `branchId` (the acting branch by
 * default; `all` for an Owner or Admin), by supplier, received dates `from`–`to` (both included), without an order, an exact
 * delivery note.
 */
export const GoodsReceiptListQuerySchema = z.object({
  branchId: z.string().trim().min(1).max(32).optional(),
  supplierId: BodyIdSchema.optional(),
  from: CalendarDaySchema.optional(),
  to: CalendarDaySchema.optional(),
  withoutOrder: Flag.optional(),
  deliveryNoteNumber: z.string().trim().min(1).max(PURCHASING_LIMITS.deliveryNoteMax).optional(),
  limit: Limit,
  cursor: z.string().max(512).optional(),
  count: Flag.optional(),
}).superRefine((v, ctx) => {
  if (v.from !== undefined && v.to !== undefined && v.to < v.from) ctx.addIssue({ code: "custom", path: ["to"], message: "the last received date is before the first", params: { code: "before_from" } });
});
export type GoodsReceiptListQuery = z.infer<typeof GoodsReceiptListQuerySchema>;
